import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

// Attach CSRF token on every mutating request. The backend mints `csrf_token`
// as a readable cookie on register/login/refresh — we echo it back in a header.
api.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (method !== "get" && method !== "head" && method !== "options") {
    const token = readCookie("csrf_token");
    if (token) cfg.headers["X-CSRF-Token"] = token;
  }
  return cfg;
});

// -------- Silent auth refresh on 401 --------
// The access-token cookie is short-lived (1h). The refresh cookie lives 30 days
// on a sliding window. When any API call comes back 401 we transparently call
// /auth/refresh once and replay the original request. This is what stops the
// installed PWA from bouncing the user to Login every time they reopen it.
//
// Guarded against loops: we never retry /auth/refresh itself, and each request
// is only retried once via the __retry flag.
let refreshInFlight = null;

function doRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = api.post("/auth/refresh")
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const url = cfg.url || "";
    // Skip the retry dance for the refresh endpoint itself, for login/register
    // (a 401 there is a real credential error), and for anything already retried.
    const skip = cfg.__retry
      || url.includes("/auth/refresh")
      || url.includes("/auth/login")
      || url.includes("/auth/register");
    if (status !== 401 || skip) return Promise.reject(error);
    try {
      await doRefresh();
    } catch {
      return Promise.reject(error);
    }
    cfg.__retry = true;
    return api(cfg);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
